import * as featureFlags from 'login-utils/featureFlags';
import * as onboardingForm from 'login-utils/onboardingForm';
import * as onboardingRoutingUtils from 'login-utils/onboardingRoutingUtils';
import { BulletedItemBase } from 'login-components/Common/PaneLayout/BulletedItem/BulletedItem';
import { LeftPaneProps } from 'login-components/Common/PaneLayout/LeftPane/LeftPane';

export type MilestonesPageType =
  | 'DEFAULT'
  | 'HELOC'
  | 'HELOAN'
  | 'CONSTRUCTION'
  | 'POST_SHORT_FORM'
  | 'UNSUPPORTED_LOAN_TYPE';

interface Page {
  left: LeftPaneProps;
  right: BulletedItemBase[];
}

type Copy = { [mt in MilestonesPageType]: Page };

const DOCUMENT_TITLE = 'Sign Up';

export const copy: Copy = {
  DEFAULT: {
    left: {
      appDocumentTitleProps: {
        title: DOCUMENT_TITLE,
      },
      title: `We'll help you every step of the way`,
      subtitle: `From applying to closing, we'll help you reach your goal.`,
      showBackButton: true,
      loginButton: 'Sign in',
    },
    right: [
      {
        title: `Tell us what you're looking for`,
        description: `Complete your profile so we can see what you may be able to afford.`,
      },
      {
        title: 'Get personalized rates',
        description: `We'll help you compare loan options you might qualify for.`,
      },
      {
        title: 'Upload documents for approval',
        description: `If you're approved, we'll support you through closing.`,
      },
    ],
  },
  HELOAN: {
    left: {
      appDocumentTitleProps: {
        title: DOCUMENT_TITLE,
      },
      title: `We'll help you every step of the way`,
      subtitle: `From applying to closing, we'll help you reach your goal.`,
      showBackButton: true,
      loginButton: 'Sign in',
    },
    right: [
      {
        title: `Tell us about the loan you're looking for`,
        description: `Provide details about your property and how much you'd like to borrow.`,
      },
      {
        title: 'Verify your information',
        description: 'Upload documents to complete your application.',
      },
      {
        title: 'Finalize your loan',
        description: `If you're approved, we'll support you through closing.`,
      },
    ],
  },
  HELOC: {
    left: {
      appDocumentTitleProps: {
        title: DOCUMENT_TITLE,
      },
      title: `We'll help you every step of the way`,
      subtitle: `From applying to closing, we'll help you reach your goal.`,
      showBackButton: true,
      loginButton: 'Sign in',
    },
    right: [
      {
        title: `Tell us what you're looking for`,
        description: `Provide details about your property and how much you'd like to borrow.`,
      },
      {
        title: 'Verify your information',
        description: 'Upload documents to complete your application.',
      },
      {
        title: 'Open your line of credit',
        description: `If you're approved, we'll help you set up your line of credit.`,
      },
    ],
  },
  CONSTRUCTION: {
    left: {
      appDocumentTitleProps: {
        title: DOCUMENT_TITLE,
      },
      title: `We'll help you every step of the way`,
      subtitle: `From applying to closing, we'll help you reach your goal.`,
      showBackButton: true,
      loginButton: 'Sign in',
    },
    right: [
      {
        title: `Tell us about the loan you're looking for`,
        description: `Provide details about your property and how much you'd like to borrow.`,
      },
      {
        title: 'Verify your information',
        description: 'Upload documents to complete your application.',
      },
      {
        title: 'Finalize your loan',
        description: `If you're approved, we'll support you through closing.`,
      },
    ],
  },
  POST_SHORT_FORM: {
    left: {
      appDocumentTitleProps: {
        title: DOCUMENT_TITLE,
      },
      title: `Based on what you've provided so far, these are your next steps`,
      subtitle: `From applying to closing, we'll help you every step of the way.`,
      showBackButton: true,
      loginButton: 'Sign in',
    },
    right: [
      {
        title: `Tell us more about what you're looking for`,
        description: `Add more details about yourself and your financial situation.`,
      },
      {
        title: `Get personalized rates`,
        description: `We'll help you compare loan options you might qualify for.`,
      },
      {
        title: `Upload documents for approval`,
        description: `If you're approved, we'll support you through closing.`,
      },
    ],
  },
  UNSUPPORTED_LOAN_TYPE: {
    left: {
      appDocumentTitleProps: {
        title: DOCUMENT_TITLE,
      },
      title: `We'll be in touch soon`,
      subtitle: `From applying to closing, we'll help you each step of the way.`,
      showBackButton: true,
      loginButton: 'Sign in',
    },
    right: [
      {
        title: `Talk to your loan team`,
        description: `We'll collect some more details about what you're looking for.`,
      },
      {
        title: `Get personalized rates`,
        description: `We'll help you compare loan options you might qualify for.`,
      },
      {
        title: `Finalize your loan`,
        description: `If you're approved, we'll help you close your loan.`,
      },
    ],
  },
};

type MortgageTypeMap = { [tv in onboardingForm.TimelineValue]?: MilestonesPageType };

const mortgageTypeMap: MortgageTypeMap = {
  NOT_STARTED: onboardingRoutingUtils.isShortFormEligible('NOT_STARTED') ? 'POST_SHORT_FORM' : 'DEFAULT',
  STILL_LOOKING: 'DEFAULT',
  NOT_IN_CONTRACT: 'DEFAULT',
  REFINANCE: 'DEFAULT',
};

export function getPageType(loanType: LoanType, timelineValue?: onboardingForm.TimelineValue): MilestonesPageType {
  if (!featureFlags.isEnabled('onboardingMilestone2_26072018')) {
    return 'DEFAULT';
  }

  switch (loanType) {
    case 'MORTGAGE':
      // fall back to DEFAULT in the off chance that the user gets into the strange,
      // supposed-to-be-impossible state where the Onboarding Milestone 2 FF is ON,
      // loanType is set to MORTGAGE, but the timelineValue is not set
      return mortgageTypeMap[timelineValue as onboardingForm.TimelineValue] || 'DEFAULT';
    case 'HELOAN':
      return 'HELOAN';
    case 'HELOC':
      return 'HELOC';
    case 'CONSTRUCTION':
      return 'CONSTRUCTION';
    default:
      return 'UNSUPPORTED_LOAN_TYPE';
  }
}

export function getMilestonesLeftPaneCopy(
  loanType: LoanType,
  timelineValue: onboardingForm.TimelineValue,
): LeftPaneProps {
  const pageType = getPageType(loanType, timelineValue);
  return copy[pageType].left;
}

export function getMilestonesRightPaneCopy(
  loanType: LoanType,
  timelineValue: onboardingForm.TimelineValue,
): BulletedItemBase[] {
  const pageType = getPageType(loanType, timelineValue);
  return copy[pageType].right;
}
